import { memo, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useFittedIncludesLine } from "../hooks/useFittedIncludesLine";
import InsightsMetricRow, {
  InsightsMetricUniformProvider,
} from "./InsightsMetricRow";
import InsightsIntensityZonesBlock from "./InsightsIntensityZonesBlock";

function metricById(metrics, id) {
  return metrics.find((m) => m.id === id);
}

/**
 * Single competition-lift card (Powerlifting, Olympic, etc.): badge, stats grid, intensity zones;
 * or “not programmed this week” when `lift.notProgrammed` is true.
 */
function InsightsPerLiftCard({ lift, insightsIntensityMeasure = "rpe" }) {
  const { t } = useTranslation("athlete-portal");
  const notProgrammed = Boolean(lift.notProgrammed);
  const isRir = insightsIntensityMeasure === "rir";
  const p = "programs.programInsights.panel";

  const metricInfo = (id) => {
    if (id === "avgPrescribedEffort") {
      return t(
        isRir
          ? `${p}.liftMetricInfo.avgPrescribedRir`
          : `${p}.liftMetricInfo.avgPrescribedRpe`
      );
    }
    return t(`${p}.liftMetricInfo.${id}`);
  };

  const liftMetricLabel = (id) => {
    if (id === "avgPrescribedEffort") {
      return t(
        isRir
          ? `${p}.liftMetrics.avgPrescribedRir`
          : `${p}.liftMetrics.avgPrescribedRpe`
      );
    }
    return t(`${p}.liftMetrics.${id}`);
  };

  const reserveDeltaSlot = useMemo(() => {
    if (notProgrammed || !lift.metrics?.length) {
      return false;
    }
    /** Any week-over-week delta (including neutral “—”) except intensity-only rows without deltas. */
    return lift.metrics.some(
      (m) => m.id !== "topIntensity" && m.delta != null
    );
  }, [lift.metrics, notProgrammed]);

  /**
   * Frequency text for the lift card.
   * @returns {string} - The frequency text for the lift card.
   */
  const frequencyText = (() => {
    const count = lift.exerciseCount;
    if (typeof count === "number" && count > 1) {
      return t(`${p}.liftFrequency.timesPerWeek`, { count });
    }
    if (lift.frequencyKey) {
      return t(`${p}.liftFrequency.${lift.frequencyKey}`);
    }
    return "";
  })();

  const { ref: includesRef, fitted: includesSummary } = useFittedIncludesLine(
    lift.includesNames,
    lift.includesNoteKey,
    lift.liftId,
    t
  );

  const hasIncludesClamp =
    Boolean(lift.includesNames?.length) || Boolean(lift.includesNoteKey);

  const cardHeader = (
    <div className="ProgramInsights-liftCardHeader">
      <span
        className={`ProgramInsights-liftBadge ProgramInsights-liftBadge--${lift.badgeVariant}`}
        aria-hidden="true"
      >
        {lift.badgeLetter}
      </span>
      <div className="ProgramInsights-liftCardTitleBlock">
        <div className="ProgramInsights-liftCardTitleRow">
          <h5 className="ProgramInsights-liftTitle">
            {t(`${p}.lifts.${lift.liftId}.name`)}
          </h5>
          <div className="ProgramInsights-liftFreq">
            <span className="ProgramInsights-liftFreqText">{frequencyText}</span>
          </div>
        </div>
        {/*
          Always reserve two lines so cards without an “includes” line match height of cards that have one.
        */}
        <p
          ref={includesRef}
          className={[
            "ProgramInsights-liftIncludes",
            hasIncludesClamp ? "ProgramInsights-liftIncludes--clamped" : "",
            hasIncludesClamp ? "" : "ProgramInsights-liftIncludes--placeholder",
          ]
            .filter(Boolean)
            .join(" ")}
          title={includesSummary?.includesTitle}
        >
          {includesSummary?.displayText ?? "\u00a0"}
        </p>
      </div>
    </div>
  );

  if (notProgrammed) {
    return (
      <div className="ProgramInsights-liftCard ProgramInsights-liftCard--notProgrammed">
        {cardHeader}
        <p className="ProgramInsights-liftCardNotProgrammed">
          {t("programs.programInsights.panel.liftNotProgrammedThisWeek")}
        </p>
      </div>
    );
  }

  const mSets = metricById(lift.metrics, "sets");
  const mReps = metricById(lift.metrics, "reps");
  const mTotalWeight = metricById(lift.metrics, "totalWeight");
  const mPercent1rm = metricById(lift.metrics, "percent1rm");
  const mAvgPrescribed = metricById(lift.metrics, "avgPrescribedEffort");
  const mTopIntensity = metricById(lift.metrics, "topIntensity");

  return (
    <div className="ProgramInsights-liftCard">
      {cardHeader}

      <InsightsMetricUniformProvider reserveDeltaSlot={reserveDeltaSlot}>
        <div className="ProgramInsights-liftMetrics ProgramInsights-liftMetrics--split">
          <div className="ProgramInsights-liftMetrics-col">
            {mSets ? (
              <InsightsMetricRow
                key={`${lift.liftId}-sets`}
                uniformLayout
                label={liftMetricLabel("sets")}
                valueDisplay={mSets.valueDisplay}
                delta={mSets.delta}
                showNeutralDeltaBadge
              />
            ) : null}
            {mTotalWeight ? (
              <InsightsMetricRow
                key={`${lift.liftId}-totalWeight`}
                uniformLayout
                truncateLabel
                label={liftMetricLabel("totalWeight")}
                valueDisplay={mTotalWeight.valueDisplay}
                delta={mTotalWeight.delta}
                infoAriaLabel={metricInfo("totalWeight")}
                showNeutralDeltaBadge
              />
            ) : null}
            {mTopIntensity ? (
              <InsightsMetricRow
                key={`${lift.liftId}-intensity`}
                uniformLayout
                label={liftMetricLabel("topIntensity")}
                valueDisplay={mTopIntensity.valueDisplay}
                infoAriaLabel={metricInfo("topIntensity")}
              />
            ) : null}
          </div>
          <div
            className="ProgramInsights-liftMetrics-rule"
            aria-hidden="true"
          />
          <div className="ProgramInsights-liftMetrics-col">
            {mReps ? (
              <InsightsMetricRow
                key={`${lift.liftId}-reps`}
                uniformLayout
                label={liftMetricLabel("reps")}
                valueDisplay={mReps.valueDisplay}
                delta={mReps.delta}
                showNeutralDeltaBadge
              />
            ) : null}
            {mPercent1rm ? (
              <InsightsMetricRow
                key={`${lift.liftId}-percent1rm`}
                uniformLayout
                label={liftMetricLabel("percent1rm")}
                valueDisplay={mPercent1rm.valueDisplay}
                delta={mPercent1rm.delta}
                infoAriaLabel={metricInfo("percent1rm")}
                showNeutralDeltaBadge
              />
            ) : null}
            {mAvgPrescribed ? (
              <InsightsMetricRow
                key={`${lift.liftId}-avgPrescribed`}
                uniformLayout
                label={liftMetricLabel("avgPrescribedEffort")}
                valueDisplay={mAvgPrescribed.valueDisplay}
                delta={mAvgPrescribed.delta}
                infoAriaLabel={metricInfo("avgPrescribedEffort")}
                showNeutralDeltaBadge
              />
            ) : null}
          </div>
        </div>
      </InsightsMetricUniformProvider>

      <InsightsIntensityZonesBlock zones={lift.intensityZones} />
    </div>
  );
}

InsightsPerLiftCard.propTypes = {
  insightsIntensityMeasure: PropTypes.oneOf(["rpe", "rir"]),
  lift: PropTypes.shape({
    liftId: PropTypes.string.isRequired,
    badgeLetter: PropTypes.string.isRequired,
    badgeVariant: PropTypes.string.isRequired,
    frequencyKey: PropTypes.string,
    exerciseCount: PropTypes.number,
    includesNames: PropTypes.arrayOf(PropTypes.string),
    includesNoteKey: PropTypes.bool,
    /** When true, metrics and intensity zones are hidden; shows empty-state copy. */
    notProgrammed: PropTypes.bool,
    metrics: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        valueDisplay: PropTypes.string.isRequired,
        delta: PropTypes.object,
      })
    ).isRequired,
    intensityZones: PropTypes.array.isRequired,
  }).isRequired,
};

export default memo(InsightsPerLiftCard);
